import { useState, useCallback } from "react";
import { X, Trash2, RefreshCw, Pencil, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AISparkles } from "@/components/ui/ai-sparkles";
import { AlternativesPanel } from "./AlternativesPanel";
import type { ZoneSelection } from "./PositioningEditor";
import { AXIS_LABEL } from "@/constants/ab-tests";
import { useZoneKeywordIntegration } from "@/resources/ab-test/ab-test.api";
import { toast } from "@/hooks/use-toast";

interface ZoneEditPanelProps {
  selection: ZoneSelection;
  onClose: () => void;
}

interface IntegrationState {
  keyword: string;
  loading: boolean;
  error: string | null;
  suggestion: { phrasing: string; rationale: string } | null;
}

export function ZoneEditPanel({ selection, onClose }: ZoneEditPanelProps) {
  const [integration, setIntegration] = useState<IntegrationState | null>(null);
  const [editingSuggestion, setEditingSuggestion] = useState<string | null>(null);
  const keywordIntegrationMutation = useZoneKeywordIntegration();

  const commit = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    selection.onReplaceText(trimmed);
    onClose();
    toast({ title: `${AXIS_LABEL[selection.axis]} updated`, description: trimmed });
  };

  const runIntegration = useCallback(
    async (keyword: string) => {
      setIntegration({ keyword, loading: true, error: null, suggestion: null });
      setEditingSuggestion(null);
      try {
        const res = await keywordIntegrationMutation.mutateAsync({
          productId: selection.productId,
          brandId: selection.brandId,
          fieldLabel: selection.fieldLabel,
          fieldText: selection.fieldText,
          zoneText: selection.zoneText,
          zoneAxis: selection.axis,
          keyword,
        });
        const phrasing = typeof res?.phrasing === "string" ? res.phrasing.trim() : "";
        const rationale = typeof res?.rationale === "string" ? res.rationale.trim() : "";
        if (!phrasing) throw new Error("AI did not return an integrated phrasing.");
        setIntegration({ keyword, loading: false, error: null, suggestion: { phrasing, rationale } });
      } catch (err) {
        setIntegration({
          keyword,
          loading: false,
          error: err instanceof Error ? err.message : "Failed to integrate keyword.",
          suggestion: null,
        });
      }
    },
    [selection, keywordIntegrationMutation],
  );

  const keywordIntegrationSlot = integration ? (
    <div className="mt-3 rounded-md bg-muted/40 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-foreground inline-flex items-center gap-1.5">
          <AISparkles className="h-3.5 w-3.5" />
          Suggested integration
        </p>
        {integration.suggestion && !integration.loading && (
          <Button type="button" variant="ghost" size="sm" className="h-7 px-2" onClick={() => runIntegration(integration.keyword)}>
            <RefreshCw className="h-3.5 w-3.5" />
            Regenerate
          </Button>
        )}
      </div>
      {integration.loading && (
        <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Working it into the {AXIS_LABEL[selection.axis].toLowerCase()} zone…
        </div>
      )}
      {integration.error && (
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-destructive">{integration.error}</p>
          <Button type="button" variant="ghost" size="sm" onClick={() => runIntegration(integration.keyword)}>Retry</Button>
        </div>
      )}
      {integration.suggestion && (
        <div className="space-y-2">
          {editingSuggestion !== null ? (
            <Textarea autoFocus value={editingSuggestion} onChange={(e) => setEditingSuggestion(e.target.value)} rows={3} className="text-sm leading-snug" />
          ) : (
            <p className="text-sm text-foreground leading-snug">{integration.suggestion.phrasing}</p>
          )}
          {integration.suggestion.rationale && (
            <p className="text-[11px] text-muted-foreground italic leading-snug">{integration.suggestion.rationale}</p>
          )}
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" onClick={() => { setEditingSuggestion(null); setIntegration(null); }}>Cancel</Button>
            {editingSuggestion === null && (
              <Button type="button" variant="outline" size="sm" onClick={() => setEditingSuggestion(integration.suggestion!.phrasing)}>
                <Pencil className="h-3.5 w-3.5" />
                Edit
              </Button>
            )}
            <Button type="button" size="sm" onClick={() => commit(editingSuggestion ?? integration.suggestion!.phrasing)}>Save</Button>
          </div>
        </div>
      )}
    </div>
  ) : undefined;

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-start justify-end gap-2 px-5 pt-4">
        <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} aria-label="Close">
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-4 space-y-5">
        <p className="text-sm text-muted-foreground leading-snug">
          Pick one of the three ways below to generate an alternative.
        </p>
        <AlternativesPanel
          axis={selection.axis}
          zoneText={selection.zoneText}
          productNameAlsoCarries={selection.productNameAxes}
          brandName={selection.brandName}
          productName={selection.productName}
          fieldLabel={selection.fieldLabel}
          fieldText={selection.fieldText}
          masterKeywords={selection.masterKeywords}
          keywordKpsScores={selection.keywordKpsScores}
          onSelect={(alt) => commit(alt.text)}
          onClose={onClose}
          productId={selection.productId ?? 0}
          brandId={selection.brandId ?? 0}
          onPickKeyword={(kw) => runIntegration(kw)}
          keywordIntegrationSlot={keywordIntegrationSlot}
          selectedKeyword={integration?.keyword ?? null}
          onResetKeyword={() => setIntegration(null)}
        />
        <div className="border-t border-border pt-3 flex items-center justify-between gap-2">
          <Button
            type="button" variant="ghost" size="sm"
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={() => { selection.onRemove(); onClose(); }}
          >
            <Trash2 className="h-3.5 w-3.5" />
            Remove element
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        </div>
      </div>
    </div>
  );
}
